import { useCallback, useContext, useEffect } from 'react';
import { ButtonGroup, Button } from 'reactstrap';
import { MapContext } from '../../../contexts/MapContext';
import { UndoIcon, RedoIcon } from '../../icons/UndoIcon';
import { mapModes } from '../../../constants/map';

const UndoRedoButtons = ({ sectionId, className }) => {
  const {
    fields: {
      undoRedo: { onUndo, onRedo, canUndo, canRedo },
    },
    mapState: {
      mapMode,
      setMapMode,
      drawRef,
      context: { keylineMode = false },
    },
  } = useContext(MapContext);

  const updateMapState = useCallback(
    ({ newSection }) => {
      if (!newSection) return;
      // Update the feature in the draw tool
      if (mapMode === mapModes.EDIT && drawRef.current) {
        const drawFeature = keylineMode
          ? newSection.rowFeature
          : newSection.fieldFeature;
        drawRef.current.delete(sectionId);
        if (drawFeature) {
          drawRef.current.add({ ...drawFeature, id: sectionId });
          drawRef.current.changeMode('direct_select', {
            featureId: sectionId,
          });
        }
      }
      // If we are in a mode that no longer makes sense now that there is
      // no field feature, switch back to section engaged mode.
      if (keylineMode && !newSection.fieldFeature) {
        setMapMode(mapModes.SECTION_ENGAGED, sectionId, {
          keylineMode: false,
        });
      } else if (
        [mapModes.EDIT, mapModes.ROTATE, mapModes.OFFSET].includes(mapMode) &&
        !newSection.fieldFeature
      ) {
        setMapMode(mapModes.SECTION_ENGAGED);
      }
    },
    [drawRef, keylineMode, mapMode, sectionId, setMapMode],
  );

  useEffect(() => {
    const undoHandler = (e) => {
      if (e.key === 'z' && (e.ctrlKey || e.metaKey)) {
        e.stopImmediatePropagation();
        if (e.shiftKey) {
          updateMapState(onRedo(sectionId));
        } else {
          updateMapState(onUndo(sectionId));
        }
      }
    };
    window.addEventListener('keydown', undoHandler);
    return () => window.removeEventListener('keydown', undoHandler);
  }, [onRedo, onUndo, sectionId, updateMapState]);

  return (
    <ButtonGroup className={className}>
      <Button
        onClick={() => updateMapState(onUndo(sectionId))}
        disabled={!canUndo}
        outline
        className="px-2 py-0"
        title="Undo (^z)"
      >
        <UndoIcon />
      </Button>
      <Button
        onClick={() => updateMapState(onRedo(sectionId))}
        disabled={!canRedo}
        outline
        className="px-2 py-0"
        title="Redo (^Z)"
      >
        <RedoIcon />
      </Button>
    </ButtonGroup>
  );
};

export default UndoRedoButtons;
